import { takeEvery, put, call, select } from "redux-saga/effects";

// Reducers
import {
  addCity,
  addZone,
  changeZoneStatus,
  deleteExistingCity,
  removeZone,
  resetZonePricingForm,
  selectCity,
  setCities,
  setCitiesFetchingStatus,
  setDistanceUnit,
  setIsZoneError,
  setIsZoneFetched,
  setIsZoneFetching,
  setNewCityCreationMode,
  setNewCityCurrencyValue,
  setNewCityCurrencyValueCorrect,
  setNewCityValue,
  setNewCityValueCorrect,
  setOnewayAccordianIndex,
  setPricingViewMode,
} from "../../redux/pricing/slice";

// API Action
import {
  createNewCityAction,
  createZoneByCityIdAction,
  deleteCityAction,
  getAllCitiesAction,
  getZonesByCityIdAction,
  removeZoneByIdAction,
  updateZonePricingByIdAction,
} from "../../api/pricing/action";

import { DISTANCE_UNIT } from "../../constants/distance-unit";
import { MODES } from "../../constants/booking-modes";

// SAGAS
function* getAllCitiesSaga(actions) {
  try {
    yield put(
      setCitiesFetchingStatus({
        status: {
          isFetching: true,
          isFetched: false,
          isError: false,
        },
      })
    );

    if (!actions.payload.token) return;

    const response = yield call(getAllCitiesAction, actions.payload);

    if (response.status === 200) {
      yield put(
        setCities({
          allCities: response.data.data,
        })
      );
      yield put(
        setCitiesFetchingStatus({
          status: {
            isFetching: false,
            isFetched: true,
            isError: false,
          },
        })
      );
    }
  } catch (error) {
    yield put(
      setCitiesFetchingStatus({
        status: {
          isFetching: false,
          isFetched: false,
          isError: true,
        },
      })
    );
  }
}

function* createCitySaga(actions) {
  if (!actions.payload.token) return;

  actions.payload.callbacks.setIsLoading(true);
  actions.payload.callbacks.setIsError(false);

  try {
    const { callbacks, ...payload } = actions.payload;
    const response = yield call(createNewCityAction, payload);
    if (response.status === 201) {
      const data = response.data.data;
      // Resetting Form and Adding City to the list
      yield put(
        addCity({
          city: {
            _id: data._id,
            cityName: data.cityName,
            currency: data.currency,
            country: data.country,
            distanceUnit: data.distanceUnit,
            longDistancePricePerUnit: data.longDistancePricePerUnit,
          },
        })
      );

      yield put(setNewCityCreationMode({ mode: false }));
      yield put(selectCity({ cityId: data._id }));

      yield put(setNewCityValueCorrect({ value: false }));
      yield put(setNewCityCurrencyValueCorrect({ value: false }));
      yield put(setNewCityCurrencyValue({ currency: "" }));
      yield put(setNewCityValue({ cityName: "", country: "" }));
      yield put(setDistanceUnit({ unit: DISTANCE_UNIT[0] }));
    }
  } catch (error) {
    actions.payload.callbacks.setIsError(true);
  } finally {
    actions.payload.callbacks.setIsLoading(false);
  }
}

function* deleteCitySaga(actions) {
  actions.payload.setErrorCallback(false);
  actions.payload.setIsLoadingCallback(true);
  try {
    const cityId = actions.payload.cityId;
    const newPayloadObj = {
      token: actions.payload.token,
      cityId: actions.payload.cityId,
    };

    if (!actions.payload.token) return;

    const response = yield call(deleteCityAction, newPayloadObj);
    if (response.status === 200) {
      yield put(deleteExistingCity({ cityId }));
    }
    actions.payload.onSuccessCallback();
  } catch (error) {
    actions.payload.setErrorCallback(true);
  } finally {
    actions.payload.setIsLoadingCallback(false);
  }
}

function* getZonesByCityIdSaga(actions) {
  const { isZoneFetched } = yield select(
    ({ pricing }) => pricing.cities[pricing.currentSelectedCityId]
  );

  if (isZoneFetched) return;

  try {
    yield put(setIsZoneError({ cityId: actions.payload.cityId, val: false }));
    yield put(setIsZoneFetching({ cityId: actions.payload.cityId, val: true }));

    const response = yield call(getZonesByCityIdAction, actions.payload);

    if (response.status === 200) {
      const zoneList = response.data.data;
      for (const zone of zoneList) {
        yield put(addZone({ newZone: zone }));
      }
      yield put(
        setIsZoneFetched({ cityId: actions.payload.cityId, val: true })
      );
    }
  } catch (err) {
    yield put(setIsZoneError({ cityId: actions.payload.cityId, val: true }));
  } finally {
    yield put(
      setIsZoneFetching({ cityId: actions.payload.cityId, val: false })
    );
  }
}

function* createZoneByCityIdSaga(actions) {
  const { callbacks, ...newPayloadObj } = actions.payload;

  try {
    actions.payload.callbacks.setIsLoading(true);
    actions.payload.callbacks.setIsError(false);

    const response = yield call(createZoneByCityIdAction, newPayloadObj); // wip
    if (response.status === 201) {
      const zone = response.data.data;

      // For Long Distance pointA & pointB is not required
      if (zone.bookingType === MODES[0]) {
        const pointA = {
          place_id: zone.pointA.place_id,
          address: zone.pointA.address,
          lat: zone.pointA.coordinates[1],
          lng: zone.pointA.coordinates[0],
        };
        const pointB = {
          place_id: zone.pointB.place_id,
          address: zone.pointB.address,
          lat: zone.pointB.coordinates[1],
          lng: zone.pointB.coordinates[0],
        };
        yield put(addZone({ newZone: { ...zone, pointA, pointB } }));
        // WIP: Might not needed
        //     yield put(setCityIsError({ cityId: zone.cityId, val: false }));
      } else {
        yield put(addZone({ newZone: zone }));
      }
      yield put(resetZonePricingForm());

      if (zone.bookingType === MODES[0]) {
        yield put(setPricingViewMode({ view: MODES[0] }));
        yield put(setOnewayAccordianIndex({ key: zone._id }));
      }

      if (actions.payload.callbacks.setIsSuccess)
        actions.payload.callbacks.setIsSuccess(true);
    }
  } catch (err) {
    actions.payload.callbacks.setIsError(true);
  } finally {
    actions.payload.callbacks.setIsLoading(false);
  }
}

function* updateZonePricingByIdSaga(actions) {
  const zoneId = actions.payload.zoneId;

  const zones = yield select(({ pricing }) => pricing.zones);

  const pricingObj = zones[zoneId]?.pricing ?? {};

  try {
    yield put(
      changeZoneStatus({
        zoneId,
        newStatus: {
          isLoading: true,
          isError: false,
        },
      })
    );

    const response = yield call(updateZonePricingByIdAction, {
      zoneId,
      pricing: pricingObj,
    });

    if (response.status === 200) {
      yield put(
        changeZoneStatus({
          zoneId,
          newStatus: {
            isChanged: false,
            isLoading: false,
            isError: false,
            isDone: true,
          },
        })
      );
    }
  } catch (err) {
    yield put(
      changeZoneStatus({
        zoneId,
        newStatus: {
          isLoading: false,
          isError: true,
        },
      })
    );
  }
}

function* removeZoneByIdSaga(actions) {
  actions.payload.callbacks.setIsZoneDeleting(true);
  actions.payload.callbacks.setIsZoneDeletingError(false);

  if (!actions.payload.zoneId || !actions.payload.token) return;

  try {
    const response = yield call(removeZoneByIdAction, {
      zoneId: actions.payload.zoneId,
      token: actions.payload.token,
    });

    if (response.status === 200) {
      yield put(removeZone({ zoneId: actions.payload.zoneId }));
      actions.payload.callbacks.setIsDialogOpen(false);
    }
  } catch (err) {
    actions.payload.callbacks.setIsZoneDeletingError(true);
  } finally {
    actions.payload.callbacks.setIsZoneDeleting(false);
  }
}

export function* watchPricingAsync() {
  yield takeEvery("EXTRANET_GET_ALL_CITIES", getAllCitiesSaga);
  yield takeEvery("EXTRANET_CREATE_CITY", createCitySaga);
  yield takeEvery("EXTRANET_DELETE_CITY", deleteCitySaga);
  yield takeEvery("EXTRANET_GET_ALL_ZONES_BY_CITY_ID", getZonesByCityIdSaga);
  yield takeEvery("EXTRANET_CREATE_ZONE_BY_CITY_ID", createZoneByCityIdSaga);
  yield takeEvery("EXTRANET_REMOVE_ZONE_BY_ID", removeZoneByIdSaga);
  yield takeEvery(
    "EXTRANET_UPDATE_ZONE_PRICNG_BY_ID",
    updateZonePricingByIdSaga
  );
}
